'use client'

import { Bell } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef, useState, useTransition } from 'react'

// Skip refreshes closer together than this. A bell badge tolerates being a few
// seconds stale, and this stops the bell hitting the DB on every single
// navigation/focus event when a user clicks around quickly.
const MIN_REFRESH_INTERVAL_MS = 30_000

export interface NotificationBellProps {
  /** Render nothing (and fetch nothing) until the session says signed in. */
  signedIn: boolean
  /** The storefront's badge-count server action. */
  getBadgeCount: () => Promise<number>
}

/**
 * The header bell: a link to /notifications with a dot when anything is new.
 * The count comes from the storefront's `getNotificationBadgeCountAction`
 * (app-shared notifications/feed), refreshed on navigation and focus.
 */
export function NotificationBell({ signedIn, getBadgeCount }: NotificationBellProps) {
  const t = useTranslations('header')
  const pathname = usePathname()
  const [count, setCount] = useState(0)
  const [, startTransition] = useTransition()
  const lastRefreshRef = useRef(0)
  const prevPathRef = useRef(pathname)

  const refresh = useCallback((force = false) => {
    const now = Date.now()
    if (!force && now - lastRefreshRef.current < MIN_REFRESH_INTERVAL_MS)
      return
    lastRefreshRef.current = now
    startTransition(async () => {
      setCount(await getBadgeCount())
    })
  }, [getBadgeCount])

  useEffect(() => {
    if (!signedIn)
      return
    // The bell is mounted persistently in the header. Force a refresh when
    // leaving /notifications (the visit marks everything read, so the badge
    // must clear); otherwise refresh is throttled to avoid a DB hit per click.
    const leftNotifications
      = prevPathRef.current.startsWith('/notifications')
        && !pathname.startsWith('/notifications')
    prevPathRef.current = pathname
    refresh(leftNotifications)

    function onFocus() {
      refresh()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [signedIn, refresh, pathname])

  if (!signedIn)
    return null

  return (
    <Link
      href="/notifications"
      className="relative flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
      aria-label={count > 0 ? t('pendingNotifications', { count }) : t('notifications')}
    >
      <Bell className="size-[18px]" />
      {count > 0 && (
        <span className="absolute right-0.5 top-0.5 flex h-2.5 w-2.5 items-center justify-center">
          <span
            className="absolute inline-flex h-full w-full rounded-full opacity-75"
            style={{ background: 'oklch(0.65 0.18 35)', animation: 'bell-ping 2s cubic-bezier(0, 0, 0.2, 1) infinite' }}
          />
          <span
            className="relative inline-flex h-2 w-2 rounded-full"
            style={{ background: 'linear-gradient(135deg, oklch(0.78 0.14 70), oklch(0.65 0.18 35))' }}
          />
        </span>
      )}
    </Link>
  )
}
